import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { useCreateUserGroup } from '../../../../../queries/UserGroup/useCreateUserGroup';

import { useDeleteUserGroupMutation } from '../../../../../queries/UserGroup/useDeleteUserGroupMutation';

// import { useDeleteUserGroupMutation } from '../../../queries/UserGroup/useDeleteUserGroupMutation';
import { invalidateUserGroups, useLoadAllUserGroups } from '../../../../../queries/UserGroup/useLoadAllUserGroups';
import { useUpdateUserGroupMutation } from '../../../../../queries/UserGroup/useUpdateUserGroupMutation';
import { Button } from '../../../../components-v2/Button';
import { Pill } from '../../../../components-v2/Pill/Pill';
import { AppBarNavigationItemPortalDestinationId } from '../../../../components/AppBar';
import { dataGridStyles } from '../../../../components/DataGrid/data-grid-styles';
import { DeleteConfirmation } from '../../../../components/DeleteConfirmation';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { NavigationBreadcrumbsBar } from '../../../../components/NavigationBreadcrumbsBar';
import { Portal } from '../../../../components/Portal';
import { ScrollView } from '../../../../components/ScrollView';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../../components/Tooltip';
import { PlusIcon } from '../../../../components/svgs/icons/PlusIcon';
import { UserGroupsIcon } from '../../../../components/svgs/icons/UserGroupsIcon';
import { INavigationItemProps } from '../../../MainContainer';
import { CreateUserGroupModal } from './createModal';
import { styles } from './styles';

interface IProps extends RouteComponentProps<any>, INavigationItemProps {}

const _ManageUserGroups = ({ routeContainerClassName }: IProps) => {
	const errorMessages = useErrorMessages();
	const [editingGroup, setEditingGroup] = React.useState<Api.IUserGroup>(null);
	const [editModalIsOpen, setEditModalIsOpen] = React.useState(false);
	const [deleteGroup, setDeleteGroup] = React.useState<Api.IUserGroup | null>(null);
	// TODO: Implement error message handling when delete is implemented
	// function errorMessage(_: string, message: string) {
	// 	displayErrorMessage(message);
	// }

	const displayErrorMessage = React.useCallback(
		(errorMsg: string) => {
			if (errorMessages) {
				errorMessages.push({
					messages: [errorMsg],
				});
			}
		},
		[errorMessages]
	);

	const userGroupLoader = useLoadAllUserGroups({
		pageSize: 500,
		expand: 'users', // Now supported on list endpoint
	});

	const createUserGroupMutation = useCreateUserGroup({
		onSuccess: () => {
			invalidateUserGroups();
			setEditModalIsOpen(false);
			setEditingGroup(null);
		},
		onError: error => {
			displayErrorMessage(error.systemMessage || 'Failed to create user group');
		},
	});

	const updateUserGroupMutation = useUpdateUserGroupMutation({
		onSuccess: () => {
			invalidateUserGroups();
			setEditModalIsOpen(false);
			setEditingGroup(null);
		},
		onError: error => {
			displayErrorMessage(error.systemMessage || 'Failed to update user group');
		},
	});

	// Auto-fetch all pages on mount
	React.useEffect(() => {
		if (userGroupLoader.hasNextPage && !userGroupLoader.isFetchingNextPage) {
			userGroupLoader.fetchNextPage();
		}
	}, [userGroupLoader]);

	// Sort user groups: predefined groups first, then custom groups
	const sortedUserGroups = React.useMemo(() => {
		if (!userGroupLoader.userGroups) return [];

		return [...userGroupLoader.userGroups].sort((a, b) => {
			// If both are predefined or both are custom, sort alphabetically
			if (a.isPredefined === b.isPredefined) {
				return a.name.localeCompare(b.name);
			}
			// Predefined groups come first
			return a.isPredefined ? -1 : 1;
		});
	}, [userGroupLoader.userGroups]);

	// Handle errors
	React.useEffect(() => {
		if (userGroupLoader.isError && userGroupLoader.error) {
			displayErrorMessage(userGroupLoader.error.message || 'Failed to load user groups');
		}
	}, [userGroupLoader.isError, userGroupLoader.error, displayErrorMessage]);

	const invalidateUsers = () => {
		userGroupLoader.refetch();
	};

	const userGroupDelete = useDeleteUserGroupMutation({
		onError: (result: Api.IOperationResultNoValue) => {
			displayErrorMessage(result.systemMessage || 'Could not delete this user');
		},
		onSuccess: () => {
			invalidateUserGroups();
			setDeleteGroup(null);
			invalidateUsers();
		},
	});

	const handleCreateGroup = () => {
		setEditingGroup({
			name: '',
			featurePermissions: {
				automation: {
					read: false,
					write: false,
					viewReporting: false,
				},
				surveys: {
					read: false,
					write: false,
					viewReporting: false,
				},
				aiPublicConversation: {
					read: false,
					write: false,
					viewReporting: false,
				},
			},
		} as Api.IUserGroup);
		setEditModalIsOpen(true);
	};

	const handleEditGroup = (group: Api.IUserGroup) => {
		setEditingGroup(group);
		setEditModalIsOpen(true);
	};

	const handleSaveGroup = async () => {
		if (!editingGroup) return;

		// Validate required fields
		if (!editingGroup.name.trim()) {
			displayErrorMessage('Group name is required');
			return;
		}

		// Prepare the user group data
		const userGroupData: Api.IUserGroup = {
			...editingGroup,
			name: editingGroup.name.trim(),
		};

		// Create or update based on whether ID exists
		if (editingGroup.id) {
			updateUserGroupMutation.mutate({ userGroup: userGroupData });
		} else {
			createUserGroupMutation.mutate({ userGroup: userGroupData });
		}
	};

	const handleCloseModal = () => {
		setEditModalIsOpen(false);
		setEditingGroup(null);
	};

	const handleGroupNameChange = (name: string) => {
		if (editingGroup) {
			setEditingGroup({ ...editingGroup, name });
		}
	};

	// Convert users/userIds back to User objects for the MultiEmployeeSelector
	const selectedUsers = React.useMemo(() => {
		if (!editingGroup) return [];

		// If we have expanded user data, use that
		if (editingGroup.users?.length) {
			const mappedUsers = editingGroup.users.map(user => {
				// Handle both email formats - string or EmailAddress object
				let primaryEmail: Api.EmailAddress | undefined;
				if (user.primaryEmail) {
					// Already an EmailAddress object
					primaryEmail = user.primaryEmail;
				} else if (user.email) {
					// Convert string email to EmailAddress format
					primaryEmail = { value: user.email };
				}

				return {
					id: user.id,
					firstName: user.firstName,
					lastName: user.lastName,
					fullName: user.fullName,
					email: user.email, // Keep the string email too
					primaryEmail,
				} as Api.IUser;
			});
			console.log('Using expanded users:', mappedUsers);
			return mappedUsers;
		}

		// Fallback to userIds if available (before expanded data loads)
		if (editingGroup.userIds?.length) {
			console.log('Waiting for expanded data, using userIds as placeholder');
			return editingGroup.userIds.map(id => ({ id }) as Api.IUser);
		}

		return [];
	}, [editingGroup]);

	const handleUserSelectionChange = (users: Api.IUser[]) => {
		if (editingGroup) {
			// Convert IUser to UserReference format for the users array
			const userReferences: Api.UserReference[] = users.map(user => ({
				id: user.id,
				email: user.email || user.primaryEmail?.value,
				primaryEmail: user.primaryEmail,
				firstName: user.firstName,
				lastName: user.lastName,
				fullName: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
				profilePic: user.profilePic,
				activationStatus: user.activationStatus,
			}));

			setEditingGroup({
				...editingGroup,
				userIds: users.map(user => user.id),
				users: userReferences,
			});
		}
	};

	const handleAutomationPermissionChange = (checked: boolean, permission: 'read' | 'write' | 'viewReporting') => {
		if (editingGroup) {
			const currentAutomation = editingGroup.featurePermissions?.automation || {
				read: false,
				write: false,
				viewReporting: false,
			};
			const newAutomationPermissions = {
				...currentAutomation,
				[permission]: checked,
			};

			// If write is being enabled, automatically enable read
			if (permission === 'write' && checked) {
				newAutomationPermissions.read = true;
			}

			// If viewReporting is being enabled, automatically enable read
			if (permission === 'viewReporting' && checked) {
				newAutomationPermissions.read = true;
			}

			// If read is being disabled, disable both write and viewReporting
			if (permission === 'read' && !checked) {
				newAutomationPermissions.write = false;
				newAutomationPermissions.viewReporting = false;
			}

			setEditingGroup({
				...editingGroup,
				featurePermissions: {
					...editingGroup.featurePermissions,
					automation: newAutomationPermissions,
				},
			});
		}
	};

	const handleAiPublicConversationPermissionChange = (
		checked: boolean,
		permission: 'read' | 'write' | 'viewReporting'
	) => {
		if (editingGroup) {
			const current = editingGroup.featurePermissions?.aiPublicConversation || {
				read: false,
				write: false,
				viewReporting: false,
			};
			const newPermissions = {
				...current,
				[permission]: checked,
			};

			if (permission === 'write' && checked) {
				newPermissions.read = true;
			}

			if (permission === 'viewReporting' && checked) {
				newPermissions.read = true;
			}

			if (permission === 'read' && !checked) {
				newPermissions.write = false;
				newPermissions.viewReporting = false;
			}

			setEditingGroup({
				...editingGroup,
				featurePermissions: {
					...editingGroup.featurePermissions,
					aiPublicConversation: newPermissions,
				},
			});
		}
	};

	// Survey permissions - Top level and per-type
	const handleSurveyPermissionChange = (
		checked: boolean,
		permission: 'read' | 'write' | 'viewReporting',
		surveyType?: 'eventRegistrationSurvey' | 'satisfactionSurvey' | 'customSurvey' | 'donationSurvey'
	) => {
		if (editingGroup) {
			const currentSurveys = editingGroup.featurePermissions?.survey || {
				read: false,
				write: false,
				viewReporting: false,
			};

			let newSurveyPermissions = { ...currentSurveys };

			if (surveyType) {
				// Update specific survey type permission
				const currentTypePermissions = currentSurveys[surveyType] || {
					read: false,
					write: false,
					viewReporting: false,
				};
				const newTypePermissions = {
					...currentTypePermissions,
					[permission]: checked,
				};

				// Apply permission hierarchy rules for specific type
				if (permission === 'write' && checked) {
					newTypePermissions.read = true;
				}
				if (permission === 'viewReporting' && checked) {
					newTypePermissions.read = true;
				}
				if (permission === 'read' && !checked) {
					newTypePermissions.write = false;
					newTypePermissions.viewReporting = false;
				}

				newSurveyPermissions = {
					...newSurveyPermissions,
					[surveyType]: newTypePermissions,
				};
			} else {
				// Update top-level permission
				newSurveyPermissions[permission] = checked;

				// Apply permission hierarchy rules for top level
				if (permission === 'write' && checked) {
					newSurveyPermissions.read = true;
				}
				if (permission === 'viewReporting' && checked) {
					newSurveyPermissions.read = true;
				}
				if (permission === 'read' && !checked) {
					newSurveyPermissions.write = false;
					newSurveyPermissions.viewReporting = false;
					// Also clear all survey-type specific permissions when disabling top-level read
					delete newSurveyPermissions.eventRegistrationSurvey;
					delete newSurveyPermissions.satisfactionSurvey;
					delete newSurveyPermissions.customSurvey;
					delete newSurveyPermissions.donationSurvey;
				}
			}

			setEditingGroup({
				...editingGroup,
				featurePermissions: {
					...editingGroup.featurePermissions,
					survey: newSurveyPermissions,
				},
			});
		}
	};

	return (
		<div className={routeContainerClassName}>
			<Portal destination={AppBarNavigationItemPortalDestinationId}>
				<NavigationBreadcrumbsBar currentLocationName='User Groups' />
			</Portal>

			<ScrollView>
				<div className={css(styles.container)}>
					<div className={css(styles.header)}>
						<div className={css(styles.headerIcon)}>
							<UserGroupsIcon />
						</div>
						<div className={css(styles.headerContent)}>
							<h1 className={css(styles.title)}>User Groups</h1>
							<p className={css(styles.description)}>Create and manage user groups with custom permissions</p>
						</div>
						<div className={css(styles.headerActions)}>
							<Button onClick={handleCreateGroup}>
								<PlusIcon fillColor='#ffffff' />
								Create New Group
							</Button>
						</div>
					</div>

					{userGroupLoader.isLoading ? (
						<LoadingSpinner type='large' />
					) : (
						<>
							{sortedUserGroups.length === 0 ? (
								<div className={css(styles.emptyState)}>
									<p>No user groups yet. Create one to get started.</p>
								</div>
							) : (
								<div className={css(styles.tableWrapper)}>
									<table className={css(dataGridStyles.table, styles.table)}>
										<thead className={css(dataGridStyles.tableHeader)}>
											<tr className={css(dataGridStyles.tableRow)}>
												<th className={css(dataGridStyles.tableHeaderCell, styles.tableHeaderCell)}>Group Name</th>
												<th className={css(dataGridStyles.tableHeaderCell, styles.tableHeaderCell)}>Type</th>
												<th
													className={css(
														dataGridStyles.tableHeaderCell,
														styles.tableHeaderCell,
														styles.tableHeaderCellActions
													)}
												>
													Actions
												</th>
											</tr>
										</thead>
										<tbody>
											{sortedUserGroups.map(group => (
												<tr key={group.id} className={css(dataGridStyles.tableRow, styles.tableRow)}>
													<td className={css(dataGridStyles.tableDataCell, styles.tableCell)}>{group.name}</td>
													<td className={css(dataGridStyles.tableDataCell, styles.tableCellType)}>
														<Pill
															label={group.isPredefined ? 'Default' : 'Custom'}
															color={group.isPredefined ? 'gray' : 'green'}
															variant='label'
														/>
													</td>
													<td className={css(dataGridStyles.tableDataCell, styles.tableCellActions)}>
														<div className={css(styles.actionButtons)}>
															<Button variant='secondary' size='sm' onClick={() => handleEditGroup(group)}>
																Edit
															</Button>

															<Tooltip>
																<TooltipTrigger asChild>
																	<Button
																		variant='destructive-secondary'
																		size='sm'
																		onClick={() => setDeleteGroup(group)}
																		styles={[group.isPredefined ? styles.tableDataCellDisabled : null]}
																		disabled={group.isPredefined}
																		title={group.isPredefined ? 'Built-in groups cannot be deleted' : 'Delete group'}
																	>
																		Delete
																	</Button>
																</TooltipTrigger>
																<TooltipContent>
																	{group.isPredefined ? 'Built-in groups cannot be deleted' : 'Delete group'}
																</TooltipContent>
															</Tooltip>
														</div>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							)}
						</>
					)}
				</div>
			</ScrollView>

			<DeleteConfirmation
				isOpen={deleteGroup !== null}
				onFinish={(shouldDelete: boolean) => {
					if (shouldDelete) {
						userGroupDelete.mutate({ userGroupId: deleteGroup.id! });
						return;
					}

					setDeleteGroup(null);
				}}
				bodyText='Are you sure you want to delete this user group?'
				destructiveButtonText='Delete'
				title='Delete User Group'
			/>

			<CreateUserGroupModal
				isOpen={editModalIsOpen}
				onClose={handleCloseModal}
				editingGroup={editingGroup}
				onGroupNameChange={handleGroupNameChange}
				onAutomationPermissionChange={handleAutomationPermissionChange}
				onAiPublicConversationPermissionChange={handleAiPublicConversationPermissionChange}
				onSurveyPermissionChange={handleSurveyPermissionChange}
				onUserSelectionChange={handleUserSelectionChange}
				onSaveGroup={handleSaveGroup}
				selectedUsers={selectedUsers}
				isPending={createUserGroupMutation.isPending || updateUserGroupMutation.isPending}
			/>
		</div>
	);
};

const ObservableManageUserGroups = observer(_ManageUserGroups);
export const ManageUserGroups = withRouter(ObservableManageUserGroups);
